"use client";

import React, { useState } from "react";

import { PunchupButton } from "@punchup/ui-common/punchup/primitives/buttons/PunchupButton";

import { EmbeddedStripeCheckout } from "./EmbeddedStripeCheckout";
import type { CheckoutServerUpdate } from "./types";
import { CHECKOUT_PAYMENT_MIN_HEIGHT_PX } from "./payment-slot-height";

export interface StableStripeCheckoutSlotProps {
  /** The session to show; null while it is being created. */
  clientSecret: string | null;
  /** The session could not be created. */
  failed: boolean;
  onRetry: () => void;
  formId: string;
  onPaymentComplete: () => void;
  onSubmittingChange: (submitting: boolean) => void;
  /** The secret of the form that just became ready, or null when it failed to load; compare it to the current session's secret. */
  onReadyChange: (secret: string | null) => void;
  onBeforeConfirm?: () => Promise<void>;
  /** Keeps the session in step with the plan on screen, in place. */
  serverUpdate?: CheckoutServerUpdate;
  skeleton: React.ReactNode;
  errorCopy: string;
  retryLabel: string;
}

/**
 * One reserved-height payment area: the session's Stripe form under a
 * skeleton until it is ready, and Retry over it when the session or the
 * form fails to load. A retried session replaces the form behind the overlay.
 */
export function StableStripeCheckoutSlot({
  clientSecret,
  failed,
  onRetry,
  formId,
  onPaymentComplete,
  onSubmittingChange,
  onReadyChange,
  onBeforeConfirm,
  serverUpdate,
  skeleton,
  errorCopy,
  retryLabel,
}: StableStripeCheckoutSlotProps) {
  const [readySecret, setReadySecret] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  const shownSecret =
    clientSecret && readySecret === clientSecret && loadFailed !== clientSecret
      ? clientSecret
      : null;

  const showError = failed || (!!clientSecret && loadFailed === clientSecret);

  return (
    <div
      data-testid="stable-stripe-checkout-slot"
      className="relative"
      style={{ minHeight: CHECKOUT_PAYMENT_MIN_HEIGHT_PX }}
    >
      {clientSecret && (
        <EmbeddedStripeCheckout
          clientSecret={clientSecret}
          formId={formId}
          onPaymentComplete={onPaymentComplete}
          onSubmittingChange={onSubmittingChange}
          onReady={() => {
            setReadySecret(clientSecret);
            onReadyChange(clientSecret);
          }}
          onLoadError={() => {
            setLoadFailed(clientSecret);
            onReadyChange(null);
          }}
          onBeforeConfirm={onBeforeConfirm}
          serverUpdate={serverUpdate}
        />
      )}
      {!shownSecret && !showError && (
        <div className="absolute inset-0 bg-white">{skeleton}</div>
      )}
      {showError && (
        <div
          role="alert"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white text-center"
        >
          <p className="text-sm text-secondary-blue">{errorCopy}</p>
          <PunchupButton variant="outline" size="sm" onClick={onRetry}>
            {retryLabel}
          </PunchupButton>
        </div>
      )}
    </div>
  );
}
